import { useState, useEffect, useRef } from "react";
import { getDashboard, getGoals } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Printer,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Target,
  Award,
  Activity,
  ArrowRight,
  Building2
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  AreaChart,
  Area,
  ComposedChart,
  ReferenceLine,
  Cell
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const YEARS = [2024, 2025, 2026, 2027];
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

// Custom colors matching Encompass Health
const COLORS = {
  primary: "#6A26B5",
  primaryLight: "#8b4fcf",
  primaryDark: "#5a1f9e",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  teal: "#00A0B0",
  gold: "#D4A84B"
};

export default function CommitteeView() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [dashData, goalsData] = await Promise.all([
          getDashboard(year),
          getGoals(year)
        ]);
        setData(dashData);
        setGoals(goalsData);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [year]);

  // Fetch logo
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings/logo`);
        const data = await res.json();
        if (data.exists && data.data) {
          setLogoUrl(`data:${data.content_type};base64,${data.data}`);
        }
      } catch (error) {
        console.error("Failed to fetch logo:", error);
      }
    };
    fetchLogo();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  const ytdRates = data?.ytd_rates || {};
  const ytdStats = data?.ytd_stats || {};
  const monthlyTrend = data?.monthly_trend || [];

  // Calculate areas for improvement (metrics not meeting goals)
  const improvements = [];
  const successes = [];

  const metrics = [
    { name: "CAUTI Rate", value: ytdRates.cauti_rate, goal: goals?.cauti_rate, unit: "/1K cath days" },
    { name: "CLABSI Rate", value: ytdRates.clabsi_rate, goal: goals?.clabsi_rate, unit: "/1K line days" },
    { name: "C.diff Rate", value: ytdRates.cdiff_rate, goal: goals?.cdiff_rate, unit: "/10K pt days" },
    { name: "MDRO Rate", value: ytdRates.mdro_rate, goal: goals?.mdro_rate, unit: "/1K pt days" },
    { name: "Total HAI Rate", value: ytdRates.total_hai_rate, goal: goals?.total_hai_rate, unit: "/1K pt days" },
    { name: "Hand Hygiene", value: ytdRates.hand_hygiene_rate, goal: goals?.hand_hygiene_compliance, unit: "%", inverse: true },
    { name: "Isolation PPE", value: ytdRates.isolation_ppe_rate, goal: goals?.isolation_ppe_compliance, unit: "%", inverse: true },
    { name: "IC EOC", value: ytdRates.ic_eoc_rate, goal: goals?.ic_eoc_compliance, unit: "%", inverse: true },
  ];

  metrics.forEach(m => {
    if (m.value === undefined || m.goal === undefined) return;
    const met = m.inverse ? m.value >= m.goal : m.value <= m.goal;
    if (met) {
      successes.push(m);
    } else {
      improvements.push(m);
    }
  });

  // Prepare radar chart data for compliance overview
  const radarData = [
    { metric: "Hand Hygiene", value: ytdRates.hand_hygiene_rate || 0, goal: goals?.hand_hygiene_compliance || 90 },
    { metric: "Isolation PPE", value: ytdRates.isolation_ppe_rate || 0, goal: goals?.isolation_ppe_compliance || 95 },
    { metric: "IC EOC", value: ytdRates.ic_eoc_rate || 0, goal: goals?.ic_eoc_compliance || 92 },
    { metric: "Dialysis EOC", value: ytdRates.dialysis_eoc_rate || 0, goal: goals?.dialysis_eoc_compliance || 90 },
  ];

  // Infection trend data with goal lines
  const infectionTrendData = monthlyTrend.map(m => ({
    ...m,
    cautiGoal: goals?.cauti_rate || 1.3,
    clabsiGoal: goals?.clabsi_rate || 0,
    cdiffGoal: goals?.cdiff_rate || 2.55,
  }));

  return (
    <div className="max-w-7xl mx-auto space-y-8" ref={printRef}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#6A26B5] via-[#4B5CD3] to-[#00A0D1] rounded-xl p-6 text-white no-print">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Organization Logo" 
                className="h-16 w-auto max-w-[200px] object-contain"
                data-testid="committee-logo"
              />
            ) : (
              <div className="h-16 w-16 rounded-lg bg-white/20 flex items-center justify-center">
                <Building2 className="w-9 h-9 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold">Infection Control Report</h1>
              <p className="text-white/80 text-sm mt-1">
                Committee Presentation • {year}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28 bg-white/20 border-white/30 text-white" data-testid="committee-year-selector">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              onClick={handlePrint}
              className="bg-white text-[#6A26B5] hover:bg-white/90"
              data-testid="print-report-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Report
            </Button>
          </div>
        </div>
      </div>

      {/* Print Header - Only shows when printing */}
      <div className="hidden print:block mb-8">
        <div className="flex items-center gap-4 border-b-4 border-[#6A26B5] pb-4">
          {logoUrl ? (
            <img src={logoUrl} alt="Organization Logo" className="h-20 w-auto max-w-[220px] object-contain" />
          ) : (
            <div className="h-20 w-20 rounded-lg bg-gradient-to-br from-[#00B4C5] to-[#6A26B5] flex items-center justify-center">
              <Building2 className="w-10 h-10 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-[#6A26B5]">Infection Control Report</h1>
            <p className="text-slate-600">Committee Presentation • {year}</p>
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-[#6A26B5] shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Patient Days YTD</p>
            <p className="text-3xl font-bold text-[#6A26B5] mt-1 font-mono">{ytdStats.patient_days?.toLocaleString() || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-[#00A0B0] shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Admissions YTD</p>
            <p className="text-3xl font-bold text-[#00A0B0] mt-1 font-mono">{ytdStats.admissions?.toLocaleString() || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Goals Met</p>
            <p className="text-3xl font-bold text-emerald-600 mt-1 font-mono">{successes.length}/{metrics.length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Areas to Improve</p>
            <p className="text-3xl font-bold text-amber-600 mt-1 font-mono">{improvements.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Areas for Improvement & Successes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Areas for Improvement */}
        <Card className="shadow-lg border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-red-500 to-orange-500 text-white py-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5" />
              Areas Requiring Attention
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {improvements.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-2" />
                All metrics meeting goals!
              </div>
            ) : (
              <div className="divide-y">
                {improvements.map((item, i) => (
                  <div key={i} className="p-4 hover:bg-red-50/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{item.name}</p>
                        <p className="text-sm text-slate-500">
                          Goal: {item.inverse ? "≥" : "≤"}{item.goal}{item.unit}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-red-600 font-mono">
                          {item.value?.toFixed(2)}{item.unit}
                        </p>
                        <div className="flex items-center gap-1 text-sm text-red-500">
                          {item.inverse ? (
                            <>
                              <TrendingDown className="w-4 h-4" />
                              {(item.goal - item.value).toFixed(2)} below goal
                            </>
                          ) : (
                            <>
                              <TrendingUp className="w-4 h-4" />
                              {(item.value - item.goal).toFixed(2)} above goal
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Successes */}
        <Card className="shadow-lg border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Award className="w-5 h-5" />
              Goals Achieved
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {successes.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                No goals met yet
              </div>
            ) : (
              <div className="divide-y">
                {successes.map((item, i) => (
                  <div key={i} className="p-4 hover:bg-emerald-50/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{item.name}</p>
                        <p className="text-sm text-slate-500">
                          Goal: {item.inverse ? "≥" : "≤"}{item.goal}{item.unit}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-600 font-mono">
                          {item.value?.toFixed(2)}{item.unit}
                        </p>
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Goal Met
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Infection Rate Trends Chart */}
      <Card className="shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Activity className="w-6 h-6 text-[#6A26B5]" />
            Monthly Infection Rate Trends
          </CardTitle>
          <p className="text-sm text-slate-500">Tracking against established goals</p>
        </CardHeader>
        <CardContent>
          {monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={infectionTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                {/* Goal Reference Lines */}
                <ReferenceLine y={goals?.cauti_rate || 1.3} stroke="#ef4444" strokeDasharray="5 5" label={{ value: "CAUTI Goal", fill: "#ef4444", fontSize: 10 }} />
                <ReferenceLine y={goals?.total_hai_rate || 2.0} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: "Total HAI Goal", fill: "#f59e0b", fontSize: 10 }} />
                {/* Actual Data */}
                <Area type="monotone" dataKey="total_hai_rate" name="Total HAI Rate" fill="#6A26B5" fillOpacity={0.1} stroke="#6A26B5" strokeWidth={3} />
                <Line type="monotone" dataKey="cauti_rate" name="CAUTI Rate" stroke="#00A0B0" strokeWidth={2} dot={{ r: 5, fill: "#00A0B0" }} />
                <Line type="monotone" dataKey="clabsi_rate" name="CLABSI Rate" stroke="#10b981" strokeWidth={2} dot={{ r: 5, fill: "#10b981" }} />
                <Line type="monotone" dataKey="cdiff_rate" name="C.diff Rate" stroke="#f59e0b" strokeWidth={2} dot={{ r: 5, fill: "#f59e0b" }} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-80 flex items-center justify-center text-slate-500">
              No monthly data available for {year}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Compliance Radar & Hand Hygiene Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compliance Radar Chart */}
        <Card className="shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Compliance Overview
            </CardTitle>
            <p className="text-sm text-slate-500">Current vs Goal Performance</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Radar name="Current" dataKey="value" stroke="#6A26B5" fill="#6A26B5" fillOpacity={0.5} strokeWidth={2} />
                <Radar name="Goal" dataKey="goal" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={2} strokeDasharray="5 5" />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Hand Hygiene Trend */}
        <Card className="shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Hand Hygiene Compliance Trend
            </CardTitle>
            <p className="text-sm text-slate-500">Monthly compliance rate vs 90% goal</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month_name" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <ReferenceLine y={90} stroke="#ef4444" strokeDasharray="5 5" label={{ value: "Goal 90%", fill: "#ef4444", fontSize: 11, position: "right" }} />
                <Tooltip 
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  formatter={(value) => [`${value?.toFixed(1)}%`, 'Hand Hygiene']}
                />
                <Bar dataKey="hand_hygiene_rate" name="Hand Hygiene %" radius={[6, 6, 0, 0]}>
                  {monthlyTrend.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.hand_hygiene_rate >= 90 ? "#10b981" : "#ef4444"} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* HAI Counts Summary */}
      <Card className="shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Year-to-Date Infection Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {[
              { label: "CAUTI", count: ytdStats.cauti_count, color: "#6A26B5" },
              { label: "CLABSI", count: ytdStats.clabsi_count, color: "#00A0B0" },
              { label: "C.diff", count: ytdStats.cdiff_count, color: "#D4A84B" },
              { label: "MDRO", count: ytdStats.mdro_count, color: "#ef4444" },
              { label: "Pneumonia", count: ytdStats.pneumonia_count, color: "#f59e0b" },
              { label: "SUTI", count: ytdStats.suti_count, color: "#8b5cf6" },
              { label: "ABUTI", count: ytdStats.abuti_count, color: "#ec4899" },
              { label: "Primary BSI", count: ytdStats.primary_bsi_count, color: "#14b8a6" },
              { label: "Skin/Soft Tissue", count: ytdStats.skin_soft_tissue_count, color: "#84cc16" },
              { label: "Total HAIs", count: ytdRates.total_hai_count, color: "#1e293b", highlight: true },
            ].map((item) => (
              <div 
                key={item.label} 
                className={`p-4 rounded-xl text-center transition-all hover:scale-105 ${
                  item.highlight ? "bg-slate-900 text-white" : "bg-slate-50"
                }`}
                style={!item.highlight ? { borderLeft: `4px solid ${item.color}` } : {}}
              >
                <p className={`text-3xl font-bold font-mono ${item.highlight ? "text-white" : ""}`} style={!item.highlight ? { color: item.color } : {}}>
                  {item.count || 0}
                </p>
                <p className={`text-xs mt-1 ${item.highlight ? "text-slate-300" : "text-slate-500"}`}>{item.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Items for Next Period */}
      <Card className="shadow-lg border-2 border-[#6A26B5]">
        <CardHeader className="bg-[#f5f0fa]">
          <CardTitle className="text-xl flex items-center gap-2 text-[#6A26B5]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Target className="w-6 h-6" />
            Recommended Focus Areas
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {improvements.length > 0 ? (
              improvements.slice(0, 3).map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">Improve {item.name}</p>
                    <p className="text-sm text-slate-600">
                      Current: {item.value?.toFixed(2)}{item.unit} → Target: {item.inverse ? "≥" : "≤"}{item.goal}{item.unit}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-amber-500" />
                </div>
              ))
            ) : (
              <div className="p-6 text-center">
                <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
                <p className="text-lg font-semibold text-emerald-700">All Goals Met!</p>
                <p className="text-slate-500 mt-2">Continue maintaining current infection prevention practices.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Footer for Print */}
      <div className="hidden print:block mt-8 pt-4 border-t-2 border-[#6A26B5]">
        <div className="flex justify-between items-center text-sm text-slate-500">
          <p>Infection Prevention & Control Report</p>
          <p>Generated: {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
}
